import { ArrowRightIcon, Button, Spinner, Typography } from '@ag1/ui';
import { useSiteCart } from '@portal/shared/components/SiteCart/hooks/SiteCartContext';
import { useMoney } from '@portal/shared/hooks/useMoney';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SiteCartFooter.module.scss';

export const SiteCartFooter = () => {
  const { cart, isCartLoading, isCartUpdating, submitCart, isDebugMode } = useSiteCart();
  const { t } = useTranslation('siteCart');
  const { formatShopifyMoney } = useMoney();

  const isCheckoutDisabled = isCartLoading || isCartUpdating;

  const hasDiscounts = useMemo(() => {
    if (!cart) return false;

    const hasCartLevelDiscount = cart.discountAllocations.length > 0;
    const hasLineLevelDiscount = cart.lines.some((line) => line.discountAllocations.length > 0);
    return hasCartLevelDiscount || hasLineLevelDiscount;
  }, [cart]);

  const originalTotalPrice = useMemo(() => {
    if (!cart) return { amount: '0', currencyCode: 'USD' };

    // Sum up each line's subtotal (pre-discount) amount
    const totalPrice = cart.lines.reduce((acc, line) => {
      return acc + Number(line.cost.subtotalAmount.amount);
    }, 0);

    return {
      amount: String(totalPrice),
      currencyCode: cart.cost.subtotalAmount.currencyCode
    };
  }, [cart]);

  const showStrikethrough = hasDiscounts && isDebugMode;

  const formatPrice = (price: { amount: string; currencyCode: string }) => {
    if (Number(price.amount) === 0) return t('free');

    return formatShopifyMoney(price);
  };

  if (!cart?.lines.length) return null;

  return (
    <div className={styles.container}>
      <div className={styles.subtotalContainer}>
        <Typography variant="portalCaption" size="1" weight="700" noPadding>
          {t('subtotalLabel')}
        </Typography>

        <div className={styles.priceContainer}>
          <Typography variant="portalCaption" size="1" weight="700" noPadding>
            {cart?.cost.subtotalAmount ? formatPrice(cart.cost.subtotalAmount) : t('free')}
          </Typography>
          {showStrikethrough && (
            <Typography variant="portalCaption" size="1" noPadding className={styles.oldPrice}>
              {formatPrice(originalTotalPrice)}
            </Typography>
          )}
        </div>
      </div>
      <Button
        rounded
        variant="primary"
        size="medium"
        icon={!isCheckoutDisabled && <ArrowRightIcon />}
        iconPosition="right"
        onClick={submitCart}
        disabled={isCheckoutDisabled}>
        {isCheckoutDisabled ? (
          <Spinner color="brand-white" size={24} withOverlay={false} />
        ) : (
          t('checkoutLabel')
        )}
      </Button>
    </div>
  );
};
